/**
 * A list menu driven by the same input frames the game reads, so a pad and the
 * keyboard both work without a separate mapping: the left stick, the d-pad,
 * W/S, or the arrows move the selection, Jump (or Enter) picks, Start, Escape,
 * or Tuck cancels, and Restart can have its own action. The mouse works too.
 * The pause menu, the course select, and the results screen are all this menu
 * with different items and content above them.
 *
 * The host snapshots held actions when a menu opens, so that opening press cannot
 * also pick or cancel. Fresh actions work immediately; closing uses `MenuGate`.
 */

import { keyToCode } from "@lache/input";
import type { InputFrame } from "@lache/sim";
import { revealMenuRow, scrollsVertically, setupMenuScroll } from "./menu-scroll";

export interface MenuItem {
  label: string;
  /** Right-aligned second column (a best time, a key hint, a setting's value). */
  detail?: string;
  /** A small picture before the label (a course's thumbnail). */
  image?: string;
  /** One line about the row, shown under the list while it is highlighted (a setting's meaning). */
  help?: string;
  /** Picked with Jump, Enter, or a click. A setting without one is stepped forward instead. */
  action?: () => void;
  /**
   * A setting: left and right (the stick, the d-pad, A/D, or the arrows) step it by `dir`. After a
   * step the menu asks its host to rebuild the items (`onChange`), so the detail shows the new value.
   */
  adjust?: (dir: 1 | -1) => void;
}

export interface ListMenuOptions {
  className: string;
  title: string;
  hint: string;
  /** The hint in touch mode (html.touch), where the items are tapped. */
  touchHint?: string;
  /** A click selects a row for preview; Jump and Enter still confirm it. Hover leaves the selection alone. */
  selectOnClick?: boolean;
  /** The list and content scroll independently, including drags in the rotated phone layout. */
  scrollPanes?: boolean;
  /** Start, Escape, or Tuck without a pick. Absent: those buttons do nothing. */
  onCancel?: () => void;
  /**
   * The label of a button in the menu's header that does what cancelling does ("Back", "Resume"), so
   * a finger or a mouse can leave every screen without a key. Absent: no button.
   */
  back?: string;
  /** The restart button while open. */
  onRestart?: () => void;
  /** The highlighted item changed. */
  onHighlight?: (index: number) => void;
  /** A setting was stepped: rebuild the items with `setItems` to show its value. */
  onChange?: () => void;
}

/** Stick deflection that counts as a step, and the repeat delay while held, ms. */
const STEP = 0.5;
const REPEAT_MS = 220;

export class ListMenu {
  readonly el: HTMLDivElement;
  /** Free space above the items for the owner's content (a time, a scoreboard). */
  readonly content: HTMLDivElement;
  readonly titleEl: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly helpEl: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;
  private items: MenuItem[] = [];
  private buttons: HTMLButtonElement[] = [];
  selected = 0;
  open = false;
  private prevPause = true;
  private prevJump = true;
  private prevTuck = true;
  private prevRestart = true;
  private prevDir = 0;
  private repeatAt = 0;
  private prevSide = 0;
  private repeatSideAt = 0;
  private readonly cancelScroll: () => void;

  constructor(
    parent: HTMLElement,
    private readonly options: ListMenuOptions,
  ) {
    this.el = document.createElement("div");
    this.el.className = `list-menu ${options.className}`;
    this.el.hidden = true;
    this.titleEl = document.createElement("div");
    this.titleEl.className = "title";
    this.titleEl.textContent = options.title;
    // The header: the title, and the way back (a click or a tap; the keys and the pad still cancel).
    const head = document.createElement("div");
    head.className = "menu-head";
    const back = document.createElement("button");
    back.type = "button";
    back.className = "menu-back";
    back.textContent = options.back ?? "";
    back.hidden = options.back === undefined || options.onCancel === undefined;
    back.addEventListener("click", (e) => {
      if (!e.defaultPrevented) options.onCancel?.();
    });
    head.append(this.titleEl, back);
    this.content = document.createElement("div");
    this.content.className = "content";
    this.list = document.createElement("div");
    this.list.className = "items";
    this.helpEl = document.createElement("div");
    this.helpEl.className = "menu-help";
    this.hintEl = document.createElement("div");
    this.hintEl.className = "hint";
    const keysHint = document.createElement("span");
    keysHint.className = "hint-keys";
    keysHint.textContent = options.hint;
    const touchHint = document.createElement("span");
    touchHint.className = "hint-touch";
    touchHint.textContent = options.touchHint ?? "tap to choose";
    this.hintEl.append(keysHint, touchHint);
    this.el.append(head, this.content, this.list, this.helpEl, this.hintEl);
    parent.appendChild(this.el);
    this.cancelScroll = setupMenuScroll(this.el, () => this.open);
    this.el.addEventListener("keydown", (e) => {
      if (!this.open) return;
      activateMenuButton(e, this.el, (button) => {
        const index = this.buttons.indexOf(button);
        if (index >= 0) {
          this.select(index);
          this.choose(index);
        } else button.click();
      });
    });
    window.addEventListener("keydown", (e) => {
      const code = keyCode(e);
      if (!this.open || (code !== "Enter" && code !== "NumpadEnter") || isTyping() || consumed(e)) return;
      e.preventDefault();
      if (e.repeat) return;
      this.choose(this.selected);
    });
  }

  setTitle(text: string): void {
    this.titleEl.textContent = text;
  }

  setItems(items: MenuItem[], selected = this.selected): void {
    this.items = items;
    this.list.replaceChildren();
    this.buttons = items.map((item, i) => {
      const b = document.createElement("button");
      b.type = "button";
      if (item.image) {
        const img = document.createElement("img");
        img.className = "thumb";
        img.src = item.image;
        img.alt = "";
        img.draggable = false;
        b.appendChild(img);
      }
      const label = document.createElement("span");
      label.className = "label";
      label.textContent = item.label;
      b.appendChild(label);
      if (item.detail !== undefined) {
        const detail = document.createElement("span");
        detail.className = "detail";
        detail.textContent = item.adjust ? `◀ ${item.detail} ▶` : item.detail;
        b.appendChild(detail);
      }
      b.addEventListener("click", (e) => {
        if (e.defaultPrevented) return;
        this.select(i);
        if (!this.options.selectOnClick) this.choose(i);
      });
      b.addEventListener("focus", () => this.select(i));
      this.list.appendChild(b);
      return b;
    });
    this.select(selected);
  }

  /** Change a row's detail without replacing its button during a pointer press or focus change. */
  setDetail(index: number, value: string): void {
    const item = this.items[index];
    const button = this.buttons[index];
    if (!item || !button) return;
    item.detail = value;
    let detail = button.querySelector<HTMLSpanElement>(".detail");
    if (!detail) {
      detail = document.createElement("span");
      detail.className = "detail";
      button.appendChild(detail);
    }
    detail.textContent = item.adjust ? `◀ ${value} ▶` : value;
  }

  select(i: number): void {
    const n = this.buttons.length;
    if (n === 0) return;
    const next = ((i % n) + n) % n;
    const changed = next !== this.selected;
    this.selected = next;
    this.buttons.forEach((b, k) => {
      b.classList.toggle("selected", k === this.selected);
      if (this.options.selectOnClick) b.setAttribute("aria-pressed", String(k === this.selected));
    });
    this.helpEl.textContent = this.items[this.selected]?.help ?? "";
    if (changed) this.scrollSelectionIntoView();
    if (changed || !this.open) this.options.onHighlight?.(this.selected);
  }

  private scrollSelectionIntoView(): void {
    const button = this.buttons[this.selected];
    if (!button) return;
    if (!this.open) return;
    if (scrollsVertically(this.list)) revealMenuRow(button, this.list);
    else if (scrollsVertically(this.el)) revealMenuRow(button, this.el);
  }

  show(selected = this.selected): void {
    this.open = true;
    this.el.hidden = false;
    // Every button counts as held until released, so the press that opened the menu does nothing here.
    this.prevPause = true;
    this.prevJump = true;
    this.prevTuck = true;
    this.prevRestart = true;
    this.prevDir = 0;
    this.prevSide = 0;
    this.select(selected);
    this.scrollSelectionIntoView();
    this.options.onHighlight?.(this.selected);
  }

  hide(): void {
    this.cancelScroll();
    this.open = false;
    this.el.hidden = true;
  }

  /** Snapshot at the actual handoff, not the first later animation frame. */
  armInput(input: InputFrame, now: number): void {
    this.prevPause = input.pause;
    this.prevJump = input.jump;
    this.prevTuck = input.tuck;
    this.prevRestart = input.restart;
    this.prevDir = input.moveY > STEP ? -1 : input.moveY < -STEP ? 1 : 0;
    this.prevSide = input.moveX > STEP ? 1 : input.moveX < -STEP ? -1 : 0;
    this.repeatAt = this.repeatSideAt = now + REPEAT_MS * 2;
  }

  private choose(i: number): void {
    const item = this.items[i];
    if (!item) return;
    if (item.action) item.action();
    else if (item.adjust) this.step(i, 1);
  }

  private step(i: number, dir: 1 | -1): void {
    const item = this.items[i];
    if (!item?.adjust) return;
    item.adjust(dir);
    this.options.onChange?.();
  }

  /** Read one input frame while open (every rendered frame). */
  update(f: InputFrame, nowMs: number): void {
    if (!this.open) return;
    const pausePressed = f.pause && !this.prevPause;
    const jumpPressed = f.jump && !this.prevJump;
    const tuckPressed = f.tuck && !this.prevTuck;
    const restartPressed = f.restart && !this.prevRestart;
    this.prevPause = f.pause;
    this.prevJump = f.jump;
    this.prevTuck = f.tuck;
    this.prevRestart = f.restart;
    // Typing a name: the letters are game keys too, so the menu ignores the frame.
    if (isTyping()) return;
    // The left stick's up and down also press Jump and Tuck (and W is both Jump and up): a press that
    // comes with the stick pushed is a step, not a pick or a cancel.
    const steering = f.moveY > STEP || f.moveY < -STEP;
    if (restartPressed && this.options.onRestart) {
      this.options.onRestart();
      return;
    }
    if ((pausePressed || (tuckPressed && !steering)) && this.options.onCancel) {
      this.options.onCancel();
      return;
    }
    if (jumpPressed && !steering) {
      this.choose(this.selected);
      return;
    }
    const dir = f.moveY > STEP ? -1 : f.moveY < -STEP ? 1 : 0;
    if (dir !== 0 && (dir !== this.prevDir || nowMs >= this.repeatAt)) {
      this.select(this.selected + dir);
      this.buttons[this.selected]?.focus({ preventScroll: true });
      this.repeatAt = nowMs + (dir !== this.prevDir ? REPEAT_MS * 2 : REPEAT_MS);
    }
    this.prevDir = dir;
    // Left and right step a setting on the highlighted row.
    const side = f.moveX > STEP ? 1 : f.moveX < -STEP ? -1 : 0;
    if (side !== 0 && (side !== this.prevSide || nowMs >= this.repeatSideAt)) {
      this.step(this.selected, side);
      this.repeatSideAt = nowMs + (side !== this.prevSide ? REPEAT_MS * 2 : REPEAT_MS);
    }
    this.prevSide = side;
  }
}

/**
 * Every screen listens for keys on the window, and a key that one screen acts on (Enter picks a row
 * that opens another screen) reaches the next screen's listener in the same dispatch. The first
 * screen marks the event, and the others leave it alone.
 */
const consumedKeys = new WeakSet<Event>();

/** Whether a screen already acted on this key event; if not, claim it. */
export function consumed(e: KeyboardEvent): boolean {
  if (consumedKeys.has(e)) return true;
  consumedKeys.add(e);
  return false;
}

/** The key's code, or one made from its key for events without one (synthetic events, some IMEs). */
export function keyCode(e: KeyboardEvent): string {
  return e.code || keyToCode(e.key);
}

/** Keep native menu buttons ahead of game-key sampling, including Space. */
export function activateMenuButton(
  e: KeyboardEvent,
  root: HTMLElement,
  activate: (button: HTMLButtonElement) => void = (button) => button.click(),
): void {
  const code = keyCode(e);
  if (e.defaultPrevented || !["Enter", "NumpadEnter", "Space"].includes(code)) return;
  const button = e.target instanceof Element ? e.target.closest("button") : null;
  if (!(button instanceof HTMLButtonElement) || !root.contains(button)) return;
  e.preventDefault();
  e.stopPropagation();
  if (!e.repeat && !button.disabled) activate(button);
}

/** True while a text field has the keyboard. */
export function isTyping(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
}

/**
 * After a menu closes, the buttons that closed it are still down; the gate keeps them from reaching
 * the simulation until they are released.
 */
export class MenuGate {
  private jump = false;
  private tuck = false;
  private pause = false;
  private restart = false;

  arm(input: InputFrame): void {
    this.jump = input.jump;
    this.tuck = input.tuck;
    this.pause = input.pause;
    this.restart = input.restart;
  }

  /** Suppress each action held when the menu closed until its release; new actions pass immediately. */
  filter(f: InputFrame): void {
    this.jump = this.jump && f.jump;
    this.tuck = this.tuck && f.tuck;
    this.pause = this.pause && f.pause;
    this.restart = this.restart && f.restart;
    if (this.jump) f.jump = false;
    if (this.tuck) f.tuck = false;
    if (this.pause) f.pause = false;
    if (this.restart) f.restart = false;
  }
}
